import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  useToast,
  Center,
  Icon,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Input,
  InputGroup,
  InputRightElement
} from '@chakra-ui/react';
import { FaTwitter, FaDiscord } from 'react-icons/fa';
import { GiPalmTree, GiIsland, GiTreasureMap, GiCoins, GiChest, GiGoldStack, GiCompass, GiPirateSkull, GiShipBow, GiAnchor, GiShipWheel } from 'react-icons/gi';

/**
 * WhitelistAccessDenied component
 * A reusable component for access denied UI based on WhitelistGuard
 * Can be used as a drop-in replacement for ComingSoonPrompt
 */
const WhitelistAccessDenied = ({ onComplete }) => {
  const { publicKey, connected } = useWallet();
  const navigate = useNavigate();
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(true);
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const passwordInputRef = useRef(null);
  
  const handlePasswordSubmit = () => {
    
    setIsSubmitting(true);
    setTimeout(() => {
      if (password === 'palm1') {
        // Store in localStorage that this user has entered correct password
        localStorage.setItem('palmheist_password_bypass', 'true');
        toast({
          title: 'Access granted',
          description: 'Welcome to Palm Heist!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        handleClose();
        if (onComplete) onComplete();
      } else {
        toast({
          title: 'Incorrect password',
          description: 'Please try again or follow social media for updates',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
      setIsSubmitting(false);
    }, 800);
  };
  
  const handleClose = () => {
    setIsOpen(false);
    navigate('/');
  };
  
  // Check if user has entered the correct password previously
  // NOTE: Automatic bypass is turned off for now, but code is kept for future use
  // const hasPasswordBypass = localStorage.getItem('palmheist_password_bypass') === 'true';
  // if (hasPasswordBypass && onComplete) {
  //   onComplete();
  //   return null;
  // }

  // Use a stable reference to the wallet address
  const walletAddress = publicKey ? publicKey.toString() : '';
  
  return (
    <Box 
      h="100vh" 
      w="100%" 
      bg="black" 
      backgroundImage="radial-gradient(circle at center, rgba(255, 94, 94, 0.2), transparent 70%)"
      position="relative"
      overflow="hidden"
      pointerEvents="auto"
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bg: "radial-gradient(circle at center, rgba(255, 94, 94, 0.15), transparent 70%)",
        pointerEvents: "none",
        zIndex: 0
      }}
    >
      {/* Animated palm trees in background */}
      <Box position="absolute" top="5%" left="5%" opacity="0.3" animation="float 8s ease-in-out infinite">
        <Icon as={GiPalmTree} w={24} h={24} color="#00e676" />
      </Box>
      <Box position="absolute" bottom="10%" right="8%" opacity="0.3" animation="float 10s ease-in-out infinite 1s">
        <Icon as={GiPalmTree} w={20} h={20} color="#00e676" />
      </Box>
      <Box position="absolute" top="40%" left="15%" opacity="0.25" animation="float 11s ease-in-out infinite 0.7s">
        <Icon as={GiCoins} w={20} h={20} color="#F6AD55" />
      </Box>
      <Box position="absolute" bottom="30%" right="20%" opacity="0.25" animation="float 9s ease-in-out infinite 1.2s">
        <Icon as={GiGoldStack} w={20} h={20} color="#F6AD55" />
      </Box>
      {/* Decorative Pirate Icons in Background */}
      <Box position="absolute" top="20px" left="20px" opacity="0.25" zIndex="0">
        <Icon as={GiCompass} color="#ff5e5e" boxSize={10} />
      </Box>
      <Box position="absolute" bottom="20px" right="20px" opacity="0.25" zIndex="0">
        <Icon as={GiAnchor} color="#ff5e5e" boxSize={10} />
      </Box>
      <Box position="absolute" top="20px" right="20px" opacity="0.25" zIndex="0">
        <Icon as={GiShipWheel} color="#ff5e5e" boxSize={10} />
      </Box>
      <Box position="absolute" bottom="20px" left="20px" opacity="0.25" zIndex="0">
        <Icon as={GiTreasureMap} color="#ff5e5e" boxSize={10} />
      </Box>
      
      <Center h="100%" zIndex="1" pointerEvents="auto">
        <Box
          maxW="650px"
          w="90%"
          p={8}
          borderRadius="xl"
          bg="rgba(20, 20, 20, 0.85)"
          border="2px solid"
          borderColor="rgba(255, 94, 94, 0.6)"
          boxShadow="0 0 60px rgba(255, 94, 94, 0.32)"
          textAlign="center"
          position="relative"
          overflow="hidden"
          pointerEvents="auto"
          _before={{
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bg: "radial-gradient(circle at center, rgba(255, 94, 94, 0.15), transparent 70%)",
            pointerEvents: "none",
            zIndex: 0
          }}
        >
          <Heading 
            color="#ff5e5e" 
            size="xl" 
            fontWeight="bold"
            textShadow="0 0 10px rgba(255, 94, 94, 0.4)"
            letterSpacing="wider"
            mb={6}
          >
            Access Denied
          </Heading>
          <VStack spacing={8} align="stretch">
            <Text
              color="#ffffff"
              fontSize="xl"
              textAlign="center"
              lineHeight="1.7"
            >
              Don't run and hide—join the crew and get inside!
            </Text>
            
            <Box 
              w="full" 
              p={5} 
              borderRadius="xl" 
              bg="rgba(255, 94, 94, 0.15)" 
              border="2px solid rgba(255, 94, 94, 0.4)"
              boxShadow="0 0 20px rgba(255, 94, 94, 0.16)"
            >
              <VStack spacing={5}>
                <Text color="white" fontWeight="bold" fontSize="lg">
                  No way in? Follow to begin!
                </Text>
                
                <HStack spacing={6} w="full" justifyContent="center" pointerEvents="auto">
                  <Button
                    variant="outline"
                    bg="rgba(29, 161, 242, 0.15)"
                    color="#ffffff"
                    borderColor="#1DA1F2"
                    borderWidth="2px"
                    borderRadius="full"
                    px={8}
                    py={6}
                    leftIcon={<FaTwitter size="20px" />}
                    _hover={{
                      bg: "rgba(29, 161, 242, 0.3)",
                      transform: "translateY(-3px)",
                      boxShadow: "0 6px 20px rgba(29, 161, 242, 0.4)"
                    }}
                    transition="all 0.3s"
                    fontSize="md"
                    fontWeight="bold"
                    cursor="pointer"
                    pointerEvents="auto"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open('https://x.com/palmheist', '_blank', 'noopener,noreferrer');
                    }}
                  >
                    Follow on Twitter
                  </Button>
                  
                  <Button
                    variant="outline"
                    bg="rgba(114, 137, 218, 0.15)"
                    color="#ffffff"
                    borderColor="#7289DA"
                    borderWidth="2px"
                    borderRadius="full"
                    px={8}
                    py={6}
                    leftIcon={<FaDiscord size="20px" />}
                    _hover={{
                      bg: "rgba(114, 137, 218, 0.3)",
                      transform: "translateY(-3px)",
                      boxShadow: "0 6px 20px rgba(114, 137, 218, 0.4)"
                    }}
                    transition="all 0.3s"
                    fontSize="md"
                    fontWeight="bold"
                    cursor="pointer"
                    pointerEvents="auto"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open('https://discord.gg/8GnWVtKth6', '_blank', 'noopener,noreferrer');
                    }}
                  >
                    Join Discord
                  </Button>
                </HStack>
              </VStack>
            </Box>
            
            {/* Completely separate password section */}
            <Box 
              w="full" 
              p={5} 
              borderRadius="xl" 
              bg="rgba(255, 94, 94, 0.15)" 
              border="2px solid rgba(255, 94, 94, 0.4)"
              boxShadow="0 0 20px rgba(255, 94, 94, 0.16)"
              mt={4}
            >
              <VStack spacing={5}>
                <Text color="white" fontSize="sm" fontWeight="bold" textAlign="center">
                  Or enter password to continue:
                </Text>
                
                <InputGroup size="md">
                  <Input
                    ref={passwordInputRef}
                    pr="4.5rem"
                    type="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handlePasswordSubmit();
                      }
                    }}
                    bg="rgba(0, 0, 0, 0.3)"
                    color="white"
                    borderColor="rgba(255, 255, 255, 0.3)"
                    _hover={{ borderColor: "rgba(255, 255, 255, 0.5)" }}
                    _focus={{ borderColor: "#ff5555", boxShadow: "0 0 0 1px #ff5555" }}
                    autoComplete="off"
                  />
                  <InputRightElement width="4.5rem">
                    <Button 
                      h="1.75rem" 
                      size="sm" 
                      onClick={handlePasswordSubmit}
                      isLoading={isSubmitting}
                      bg="#ff5555"
                      color="white"
                      _hover={{ bg: "#ff3333" }}
                    >
                      Enter
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </VStack>
            </Box>
            
            {walletAddress && (
              <>
                <Divider borderColor="rgba(255, 255, 255, 0.2)" />
                <Text color="whiteAlpha.700" fontSize="sm" textAlign="center">
                  Wallet: {walletAddress}
                </Text>
              </>
            )}
            
            <Text 
              color="#ffffff" 
              fontSize="md" 
              textAlign="center"
              fontStyle="italic"
            >
              While they wait, you're stacking high—whitelisted winners touch the sky!
            </Text>
            
            <Button 
              colorScheme="purple" 
              variant="outline"
              bg="rgba(153, 69, 255, 0.15)"
              color="#ffffff"
              borderColor="#9945FF"
              borderWidth="2px"
              borderRadius="full"
              px={8}
              py={5}
              mx="auto"
              maxW="200px"
              onClick={handleClose}
              _hover={{
                bg: "rgba(153, 69, 255, 0.3)",
                transform: "translateY(-3px)",
                boxShadow: "0 6px 20px rgba(153, 69, 255, 0.4)"
              }}
              transition="all 0.3s"
              fontSize="md"
              fontWeight="bold"
              textShadow="0 0 8px rgba(153, 69, 255, 0.24)"
            >
              Back to Home
            </Button>
          </VStack>
        </Box>
      </Center>
    </Box>
  );
};

export default WhitelistAccessDenied;
